import { DEFAULT_CART_ANIMATION_CONFIG, type CartAnimationConfig } from './cart-fly-item.constants';
import { applyCloneBaseStyles, getClosest, miniCartShake, supportsElementAnimation } from './cart-fly-item.dom';
import { buildArcMotionFrames, clamp } from './cart-fly-item.motion';

export interface CartAnimationParams {
    triggerEvent?: Event | React.MouseEvent<HTMLElement> | null;
    imageElement?: HTMLElement | null;
    miniCartElement?: HTMLElement | null;
    config?: Partial<CartAnimationConfig>;
}

export const cartAnimation = async ({
    triggerEvent,
    imageElement,
    miniCartElement,
    config = {},
}: CartAnimationParams): Promise<void> => {
    if (typeof document === 'undefined') {
        return;
    }

    const {
        productCardSelector,
        productImageSelector,
        miniCartSelector,
        duration,
        startScale,
        midScale,
        endScale,
        arcHeight,
        rotateDeg,
        cloneSize,
    } = {
        ...DEFAULT_CART_ANIMATION_CONFIG,
        ...config,
    };

    let resolvedImageElement: HTMLElement | null = imageElement ?? null;

    if (!resolvedImageElement && triggerEvent?.target) {
        const productCard = getClosest(triggerEvent.target as Element, productCardSelector);

        if (productCard) {
            resolvedImageElement = productCard.querySelector<HTMLElement>(productImageSelector);
        }
    }

    const resolvedMiniCartElement = miniCartElement ?? document.querySelector<HTMLElement>(miniCartSelector);

    if (!resolvedImageElement || !resolvedMiniCartElement) {
        return;
    }

    if (!supportsElementAnimation(resolvedImageElement) || !supportsElementAnimation(resolvedMiniCartElement)) {
        return;
    }

    const imageRect = resolvedImageElement.getBoundingClientRect();
    const targetRect = resolvedMiniCartElement.getBoundingClientRect();

    const baseSize = clamp(cloneSize, 56, 96);

    const startX = imageRect.left + imageRect.width / 2 - baseSize / 2;
    const startY = imageRect.top + imageRect.height / 2 - baseSize / 2;

    const endX = targetRect.left + targetRect.width / 2 - baseSize / 2;
    const endY = targetRect.top + targetRect.height / 2 - baseSize / 2;

    const clone = resolvedImageElement.cloneNode(true) as HTMLElement;

    applyCloneBaseStyles({
        clone,
        baseSize,
        startX,
        startY,
        startScale,
    });

    document.body.appendChild(clone);

    const flyFrames = buildArcMotionFrames({
        start: { x: startX, y: startY },
        end: { x: endX, y: endY },
        startScale,
        midScale,
        endScale,
        arcHeight,
        rotateDeg,
    });

    const flyAnimation = clone.animate(flyFrames, {
        duration,
        easing: 'linear',
        fill: 'forwards',
        composite: 'replace',
    });

    try {
        await flyAnimation.finished;
    } catch {
        if (document.body.contains(clone)) {
            clone.remove();
        }

        return;
    }

    if (!document.body.contains(clone)) {
        return;
    }

    miniCartShake(resolvedMiniCartElement);
    clone.remove();
};
